import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";

import { TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "../wiedervorlagen/constants";
import { isDocumentWithIdAndProcessMetadata, isDocumentWithProcessMetadata } from "../workflows/data/processMetadata";

import { LINKS_ACTIVITY } from "./constants";
import { dataModels } from "./moduleResources";

/**
 * Zeigt die Overview für verknüpte Vorgänge an oder versteckt sie wieder
 */
export function* openLinksSaga(): SagaGenerator<void> {
	yield* takeLatest((action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButtonTriggered.match(action.payload.engineEvent) &&
			action.payload.engineEvent.payload.name === "showLinks" &&
			!!action.payload.engineEvent.payload.buttonPath
		);
	}, openLinks);
}

function* openLinksActivity(
	activityDescriptor: {
		view: string;
		instance: undefined;
		processId: any;
		fachverfahren: string | undefined;
		model: string;
	},
	action: Action<FormEngineActions.FormEngineEventActions>
) {
	const wiedervorlagenActivity = yield* select(ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY));
	if (wiedervorlagenActivity) {
		yield* put(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
	}
	const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
	const isFinished =
		FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
			? data.document.root.processMetadata.isFinished
			: false;

	const sourceProcessId =
		FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
			? data.document.root.processMetadata.links?.sourceProcessId
			: undefined;

	const taskUiState = yield* select(FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID));
	const isTaskReadOnly = (taskUiState?.readonly ?? false) && !isFinished;
	yield* put(
		ActivityActions.create({
			activityDescriptor: { ...activityDescriptor, sourceProcessId },
			initiatingActivityId: action.payload.activityId,
			activityId: LINKS_ACTIVITY,
			slices: {
				uiState: {
					readonly: isTaskReadOnly
				}
			}
		})
	);
}

function* openLinks(action: Action<FormEngineActions.FormEngineEventActions>): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(action.payload.activityId));

	if (!activity) {
		throw new Error("Keine Activity gefunden.");
	}

	const sourceDescriptor = { ...(activity?.descriptor ?? {}) } as Record<string, string | undefined>;

	const processId = yield* call(getProcessIDFromActivity, activity.id);

	if (!processId) {
		console.error("No process ID in task activity");
		return;
	}

	const fvId = sourceDescriptor.fachverfahren;
	const activityDescriptor = {
		fachverfahren: fvId,
		instance: undefined,
		model: dataModels.GENERIC_PROCESS_MODEL,
		processId,
		reference: activity.descriptor.instance,
		view: "links"
	};

	const wasOpenBefore = yield* select(ActivitySelectors.activityById(LINKS_ACTIVITY));
	if (wasOpenBefore) {
		yield* put(ActivityActions.cancel({ activityId: LINKS_ACTIVITY }));
	} else {
		yield* call(openLinksActivity, activityDescriptor, action);
	}
}

function* getProcessIDFromActivity(activityId: string): SagaGenerator<string | undefined> {
	const data = yield* select(ActivitySelectors.data(activityId));
	if (FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithIdAndProcessMetadata(data.document)) {
		return data.document.root.processMetadata.processId;
	}
	return undefined;
}

export function* closeLinksSaga(): SagaGenerator<void> {
	yield* takeLatest(WorkflowsActions.completeTask.done, function* () {
		const linksActivity = yield* select(ActivitySelectors.activityById(LINKS_ACTIVITY));
		if (linksActivity) {
			yield* put(ActivityActions.cancel({ activityId: LINKS_ACTIVITY }));
		}
	});
}

export const linksSagas = [openLinksSaga, closeLinksSaga];
